import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaWrench, 
  FaFilePdf, 
  FaPlayCircle, 
  FaCheckCircle, 
  FaExclamationTriangle,
  FaClock,
  FaDownload,
  FaChevronDown,
  FaChevronUp,
  FaListOl,
  FaTools,
  FaShieldAlt
} from 'react-icons/fa';

const InstallationGuide = ({ product }) => {
  const [activeTab, setActiveTab] = useState('step-by-step');
  const [expandedSteps, setExpandedSteps] = useState({});

  // Default installation guide data (can be replaced with product-specific data from API)
  const getInstallationGuide = () => {
    // This would typically come from product data or API
    // For now, we'll generate based on product category
    const category = product?.category || 'Maintenance Service Parts';
    const partType = product?.subCategory || product?.class || 'Parts';

    // Common installation steps for automotive parts
    const commonSteps = [
      {
        step: 1,
        title: 'Safety First',
        description: 'Ensure the vehicle is parked on a level surface with the parking brake engaged. Disconnect the battery if working near electrical components.',
        tools: ['Safety glasses', 'Gloves', 'Jack stands'],
        warnings: ['Always work in a well-ventilated area', 'Never work under a vehicle supported only by a jack'],
        time: '5 minutes',
        image: null
      },
      {
        step: 2,
        title: 'Locate the Component',
        description: 'Identify the location of the part to be replaced. Refer to your vehicle\'s service manual for specific location details.',
        tools: ['Flashlight', 'Service manual'],
        warnings: [],
        time: '10 minutes',
        image: null
      },
      {
        step: 3,
        title: 'Remove Old Component',
        description: 'Carefully remove the old component following the reverse order of installation. Keep track of all bolts and fasteners.',
        tools: ['Socket set', 'Wrench set', 'Screwdriver'],
        warnings: ['Do not force components', 'Label connections if needed'],
        time: '20-30 minutes',
        image: null
      },
      {
        step: 4,
        title: 'Install New Component',
        description: 'Install the new component in the reverse order of removal. Ensure all connections are secure and properly tightened.',
        tools: ['Torque wrench', 'Socket set'],
        warnings: ['Follow manufacturer torque specifications', 'Do not overtighten'],
        time: '20-30 minutes',
        image: null
      },
      {
        step: 5,
        title: 'Test and Verify',
        description: 'Reconnect the battery if disconnected. Start the vehicle and test the new component. Check for any leaks or unusual sounds.',
        tools: [],
        warnings: ['Test in a safe area', 'Monitor for any issues'],
        time: '10 minutes',
        image: null
      }
    ];

    // Category-specific modifications
    if (category.includes('Brake') || partType.includes('Brake')) {
      return {
        title: 'Brake System Installation Guide',
        estimatedTime: '1-2 hours',
        difficulty: 'Intermediate',
        tools: [
          'Jack and jack stands',
          'Lug wrench',
          'Socket set',
          'C-clamp or brake piston tool',
          'Torque wrench',
          'Brake cleaner',
          'Safety glasses',
          'Gloves'
        ],
        steps: [
          {
            step: 1,
            title: 'Prepare the Vehicle',
            description: 'Park on level ground, engage parking brake, and chock the wheels. Loosen lug nuts before lifting the vehicle.',
            tools: ['Jack', 'Jack stands', 'Wheel chocks'],
            warnings: ['Never work under a vehicle on a jack alone', 'Always use jack stands'],
            time: '10 minutes'
          },
          {
            step: 2,
            title: 'Remove the Wheel',
            description: 'Lift the vehicle and secure with jack stands. Remove the lug nuts and wheel.',
            tools: ['Lug wrench', 'Jack stands'],
            warnings: [],
            time: '5 minutes'
          },
          {
            step: 3,
            title: 'Remove Old Brake Pads',
            description: 'Remove the caliper bolts and carefully lift the caliper. Remove old brake pads and inspect the rotor.',
            tools: ['Socket set', 'C-clamp'],
            warnings: ['Do not let the caliper hang by the brake line', 'Inspect rotor for damage'],
            time: '15 minutes'
          },
          {
            step: 4,
            title: 'Install New Brake Pads',
            description: 'Compress the caliper piston if needed. Install new brake pads and reinstall the caliper.',
            tools: ['C-clamp or brake piston tool', 'Socket set'],
            warnings: ['Ensure pads are properly seated', 'Check pad orientation'],
            time: '20 minutes'
          },
          {
            step: 5,
            title: 'Reinstall Wheel and Test',
            description: 'Reinstall the wheel and lower the vehicle. Test brakes before driving. Bed in new pads as recommended.',
            tools: ['Lug wrench', 'Torque wrench'],
            warnings: ['Torque lug nuts to specification', 'Test brakes at low speed first'],
            time: '15 minutes'
          }
        ],
        videoUrl: null,
        pdfUrl: null
      };
    }

    // Default guide
    return {
      title: `${product?.name || 'Product'} Installation Guide`,
      estimatedTime: '1-2 hours',
      difficulty: 'Intermediate',
      tools: [
        'Basic socket set',
        'Wrench set',
        'Screwdriver set',
        'Torque wrench',
        'Safety glasses',
        'Gloves',
        'Jack and jack stands (if needed)'
      ],
      steps: commonSteps,
      videoUrl: null,
      pdfUrl: null
    };
  };

  const guide = getInstallationGuide();

  const toggleStep = (stepNumber) => {
    setExpandedSteps(prev => ({
      ...prev,
      [stepNumber]: !prev[stepNumber]
    }));
  };

  const tabs = [
    { id: 'step-by-step', label: 'Step-by-Step', icon: FaListOl },
    { id: 'tools', label: 'Tools Required', icon: FaTools },
    { id: 'safety', label: 'Safety Info', icon: FaShieldAlt }
  ];

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
          <FaWrench className="text-[#131c36]" />
          Installation Guide & Manuals
        </h2>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <FaClock className="text-gray-400" />
          <span>{guide.estimatedTime}</span>
          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
            {guide.difficulty}
          </span>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-4">
        <div className="flex flex-wrap gap-2">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-[#131c36] text-[#131c36]'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                }`}
              >
                <Icon className="text-sm" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        {activeTab === 'step-by-step' && (
          <motion.div
            key="step-by-step"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-4"
          >
            {/* Video Guide (if available) */}
            {guide.videoUrl && (
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-3 mb-2">
                  <FaPlayCircle className="text-red-600 text-xl" />
                  <h3 className="font-semibold text-gray-900">Video Installation Guide</h3>
                </div>
                <div className="aspect-video bg-gray-200 rounded-lg flex items-center justify-center">
                  <a
                    href={guide.videoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
                  >
                    <FaPlayCircle className="text-4xl" />
                    <span>Watch Installation Video</span>
                  </a>
                </div>
              </div>
            )}

            {/* PDF Download (if available) */}
            {guide.pdfUrl && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FaFilePdf className="text-red-600 text-xl" />
                    <div>
                      <h3 className="font-semibold text-gray-900">Installation Manual (PDF)</h3>
                      <p className="text-sm text-gray-600">Download the complete installation guide</p>
                    </div>
                  </div>
                  <a
                    href={guide.pdfUrl}
                    download
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <FaDownload />
                    <span className="hidden sm:inline">Download</span>
                  </a>
                </div>
              </div>
            )}

            {/* Step-by-Step Instructions */}
            <div className="space-y-3">
              <h3 className="font-semibold text-gray-900 mb-3">Installation Steps</h3>
              {guide.steps.map((step, index) => (
                <motion.div
                  key={step.step}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="border border-gray-200 rounded-lg overflow-hidden"
                >
                  <button
                    onClick={() => toggleStep(step.step)}
                    className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-[#131c36] text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">
                        {step.step}
                      </div>
                      <div className="text-left">
                        <h4 className="font-semibold text-gray-900">{step.title}</h4>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-xs text-gray-600 flex items-center gap-1">
                            <FaClock className="text-xs" />
                            {step.time}
                          </span>
                          {step.tools && step.tools.length > 0 && (
                            <span className="text-xs text-gray-600 flex items-center gap-1">
                              <FaTools className="text-xs" />
                              {step.tools.length} tool{step.tools.length > 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    {expandedSteps[step.step] ? (
                      <FaChevronUp className="text-gray-400" />
                    ) : (
                      <FaChevronDown className="text-gray-400" />
                    )}
                  </button>

                  <AnimatePresence>
                    {expandedSteps[step.step] && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="p-4 bg-white border-t border-gray-200">
                          <p className="text-sm text-gray-700 mb-4">{step.description}</p>

                          {step.tools && step.tools.length > 0 && (
                            <div className="mb-4">
                              <h5 className="text-xs font-semibold text-gray-900 mb-2 flex items-center gap-2">
                                <FaTools className="text-gray-600" />
                                Tools Required:
                              </h5>
                              <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                                {step.tools.map((tool, idx) => (
                                  <li key={idx}>{tool}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {step.warnings && step.warnings.length > 0 && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                              <h5 className="text-xs font-semibold text-yellow-900 mb-2 flex items-center gap-2">
                                <FaExclamationTriangle className="text-yellow-600" />
                                Important Warnings:
                              </h5>
                              <ul className="list-disc list-inside text-sm text-yellow-800 space-y-1">
                                {step.warnings.map((warning, idx) => (
                                  <li key={idx}>{warning}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {step.image && (
                            <div className="mt-4">
                              <img loading="lazy"
                                src={step.image}
                                alt={`Step ${step.step}`}
                                className="w-full rounded-lg border border-gray-200"
                              />
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>

            {/* Completion Note */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
              <div className="flex items-start gap-3">
                <FaCheckCircle className="text-green-600 text-xl flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-green-900 mb-1">Installation Complete!</h4>
                  <p className="text-sm text-green-800">
                    After installation, test the component thoroughly. If you encounter any issues, 
                    consult a professional mechanic or refer to your vehicle's service manual.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {activeTab === 'tools' && (
          <motion.div
            key="tools"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <h3 className="font-semibold text-gray-900 mb-4">Required Tools & Equipment</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {guide.tools.map((tool, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200"
                >
                  <FaTools className="text-[#131c36] flex-shrink-0" />
                  <span className="text-sm text-gray-700">{tool}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> Some tools may be available for rent at local auto parts stores. 
                Ensure all tools are in good condition before starting the installation.
              </p>
            </div>
          </motion.div>
        )}

        {activeTab === 'safety' && (
          <motion.div
            key="safety"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-4"
          >
            <h3 className="font-semibold text-gray-900 mb-4">Safety Information</h3>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-3 mb-3">
                <FaExclamationTriangle className="text-red-600 text-xl flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-red-900 mb-2">Critical Safety Warnings</h4>
                  <ul className="list-disc list-inside text-sm text-red-800 space-y-1">
                    <li>Always work in a well-ventilated area</li>
                    <li>Never work under a vehicle supported only by a jack - always use jack stands</li>
                    <li>Disconnect the battery when working near electrical components</li>
                    <li>Wear appropriate safety gear (gloves, safety glasses, etc.)</li>
                    <li>Allow the vehicle to cool before working on hot components</li>
                    <li>Follow all manufacturer torque specifications</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-3 mb-3">
                <FaShieldAlt className="text-yellow-600 text-xl flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-yellow-900 mb-2">Pre-Installation Checklist</h4>
                  <ul className="list-disc list-inside text-sm text-yellow-800 space-y-1">
                    <li>Park vehicle on level ground with parking brake engaged</li>
                    <li>Chock wheels if working on brakes or suspension</li>
                    <li>Gather all required tools before starting</li>
                    <li>Read the installation guide completely</li>
                    <li>Ensure you have adequate lighting</li>
                    <li>Have a fire extinguisher nearby</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <FaCheckCircle className="text-blue-600 text-xl flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-blue-900 mb-2">When to Seek Professional Help</h4>
                  <p className="text-sm text-blue-800 mb-2">
                    If you're unsure about any step or encounter unexpected issues, it's best to consult a professional mechanic. 
                    Some installations require specialized knowledge and equipment.
                  </p>
                  <p className="text-sm text-blue-800">
                    <strong>Remember:</strong> Safety should always be your top priority. When in doubt, seek professional assistance.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default InstallationGuide;

